import { Box, Card, CardContent, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { useEffect, useMemo, useRef, useState } from "react";
import {
	DEFAULT_INCENTIVE_THRESHOLDS,
	getZoneColor,
	SURFACE,
	TEXT,
} from "@/pages/incentive/constants/colors";
import { IncPercentPill } from "@/pages/incentive/shared/components/IncPercentPill";
import type { IncentiveTankData } from "../models/MillOverview2";

type Props = {
	data: IncentiveTankData | null;
};

export const IncentiveTankChart = ({ data }: Props) => {
	const thresholds = data
		? {
				Red: data.IncRed,
				Orange: data.IncOrange,
				Yellow: data.IncYellow,
				GreenSoft: data.IncGreenSoft,
				GreenNormal: data.IncGreenNormal,
				GreenHard: data.IncGreenHard,
			}
		: DEFAULT_INCENTIVE_THRESHOLDS;
	const containerRef = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState(0);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const ro = new ResizeObserver(([entry]) => {
			setHeight(entry.contentRect.height);
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	const finished = data?.Finished ?? 0;
	const target = data?.Target ?? 0;
	const incPercent = data?.IncPercent ?? null;

	const noData = incPercent == null || (incPercent === 0 && target === 0);
	const pct = incPercent ?? 0;
	const zoneColor = getZoneColor(noData ? null : pct, thresholds);
	const fillColor = zoneColor.bg;

	const maxVal = Math.max(finished, target, 1);
	const targetColor = "#42A5F5";

	const chartOptions = useMemo<EChartsOption>(
		() => ({
			tooltip: {
				show: true,
				formatter: () =>
					`Finished Tons: ${finished.toFixed(0)}<br/>Target Tons: ${target.toFixed(0)}<br/>Incentive: ${pct.toFixed(1)}%`,
				borderColor: fillColor,
				appendTo: document.body,
			},
			grid: {
				left: 5,
				right: 5,
				top: 20,
				bottom: 5,
				containLabel: false,
			},
			xAxis: {
				type: "category" as const,
				data: ["Finished", "Target"],
				show: false,
			},
			yAxis: {
				type: "value" as const,
				min: 0,
				max: maxVal,
				show: false,
			},
			series: [
				{
					type: "bar",
					data: [
						{
							value: noData ? 0 : finished,
							itemStyle: {
								color: fillColor,
								borderRadius: [4, 4, 0, 0],
							},
						},
						{
							value: target,
							itemStyle: {
								color: targetColor,
								borderRadius: [4, 4, 0, 0],
							},
						},
					],
					barWidth: "70%",
					barMinHeight: 4,
					label: {
						show: true,
						position: "top",
						formatter: (params: { dataIndex?: number }) => {
							if (params.dataIndex === 0) return `${finished.toFixed(0)}`;
							return `${target.toFixed(0)}`;
						},
						fontSize: 11,
						fontWeight: 600,
						color: "#757575",
					},
					silent: false,
				},
			],
		}),
		[finished, target, fillColor, noData, pct, maxVal],
	);

	return (
		<Card elevation={1} sx={{ height: "100%" }}>
			<CardContent
				sx={{
					p: 1,
					"&:last-child": { pb: 1 },
					display: "flex",
					flexDirection: "column",
					height: "100%",
				}}
			>
				<Typography
					variant="subtitle2"
					sx={{
						fontWeight: 700,
						textAlign: "center",
						mb: 0.5,
					}}
				>
					Incentive
				</Typography>
				<Box
					ref={containerRef}
					sx={{
						flex: 1,
						minHeight: 0,
						border: `1px solid ${SURFACE.borderLight}`,
						borderRadius: 1,
						backgroundColor: SURFACE.background,
						overflow: "hidden",
					}}
				>
					{height > 0 && (
						<ReactECharts
							option={chartOptions}
							notMerge
							style={{ height, width: "100%" }}
							opts={{ renderer: "svg" }}
						/>
					)}
				</Box>
				<Box sx={{ display: "flex", justifyContent: "center", mt: 0.5 }}>
					<IncPercentPill
						value={noData ? 0 : incPercent}
						thresholds={thresholds}
						isFuture={noData}
					/>
				</Box>
			</CardContent>
		</Card>
	);
};
